import { createContext, useContext, useMemo, type ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api, setCustomersApiBaseUrl } from '@/lib/api'
import type { CurrentTeam, Feature } from '@/lib/types'
import { useAuth } from '@/features/auth/use-auth'

interface CurrentTeamValue {
  current: CurrentTeam | null
  loading: boolean
  /** true once the customers API base URL points at this team's instance */
  ready: boolean
  refetch: () => void
  isAdmin: boolean
  hasFeature: (f?: Feature) => boolean
}

export const CurrentTeamContext = createContext<CurrentTeamValue | null>(null)

export function CurrentTeamProvider({ children }: { children: ReactNode }) {
  const { user, loading: authLoading } = useAuth()
  const qc = useQueryClient()

  const query = useQuery({
    queryKey: ['team', 'current', user?.uid],
    enabled: !!user,
    queryFn: async () => {
      const { data } = await api.get<CurrentTeam>('/team/current')
      setCustomersApiBaseUrl(data.team?.CustomersApiUrl)
      return data
    },
  })

  const value = useMemo<CurrentTeamValue>(() => {
    const current = query.data ?? null
    return {
      current,
      loading: authLoading || (!!user && query.isPending),
      ready: !!current,
      refetch: () => qc.invalidateQueries({ queryKey: ['team'] }),
      isAdmin: current?.role === 'admin',
      // Mirrors v1 usePlanFeatures: no requirement, or still loading → allowed
      hasFeature: (f) => !f || !current || (current.plan?.Features ?? []).includes(f),
    }
  }, [query.data, query.isPending, authLoading, user, qc])

  return <CurrentTeamContext.Provider value={value}>{children}</CurrentTeamContext.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components
export function useCurrentTeam() {
  const ctx = useContext(CurrentTeamContext)
  if (!ctx) throw new Error('useCurrentTeam must be used inside CurrentTeamProvider')
  return ctx
}
